import type { ControlBindings } from '../sim/save';
import type { Direction, InputFrame } from '../sim/types';
import { DirectionRepeatGate, pickLatestDirection } from './direction-priority';

type Action = keyof ControlBindings;

const P2_KEYS: Readonly<Record<string, string>> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  Numpad0: 'wall',
  Numpad1: 'shatter',
  Numpad2: 'special',
};

export class InputManager {
  private bindings: ControlBindings;
  private readonly held = new Set<string>();
  private readonly pressed = new Set<string>();
  private readonly touchHeld = new Set<string>();
  private readonly touchPressed = new Set<string>();
  private readonly gamepadHeld = new Set<string>();
  private readonly gamepadPressed = new Set<string>();
  private readonly directionPriority = new Map<string, number>();
  private readonly directionRepeat = {
    1: new DirectionRepeatGate(),
    2: new DirectionRepeatGate(),
  } as const;
  private inputSequence = 0;
  private captureResolver: ((code: string) => void) | null = null;

  constructor(bindings: ControlBindings) {
    this.bindings = { ...bindings };
    window.addEventListener('keydown', this.onKeyDown, { passive: false });
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', () => {
      this.held.clear();
      this.touchHeld.clear();
      this.gamepadHeld.clear();
      this.directionPriority.clear();
      this.directionRepeat[1].reset();
      this.directionRepeat[2].reset();
    });
  }

  setBindings(bindings: ControlBindings): void {
    this.bindings = { ...bindings };
  }

  poll(playerCount: 1 | 2): [InputFrame, InputFrame] {
    this.pollGamepads();
    const first = this.keyboardFrame(1, playerCount === 1);
    const second = this.keyboardFrame(2, false);
    this.pressed.clear();
    this.touchPressed.clear();
    this.gamepadPressed.clear();
    for (const key of [...this.directionPriority.keys()]) {
      if (key.startsWith('key:') && !this.held.has(key.slice(4))) this.directionPriority.delete(key);
      if (key.startsWith('touch:') && !this.touchHeld.has(key.slice(6))) this.directionPriority.delete(key);
    }
    return [first, second];
  }

  consumePause(): boolean {
    this.pollGamepads();
    return this.consumeCode(this.bindings.pause) || this.consumeGamepad('0:9');
  }

  consumeRestart(): boolean {
    return this.consumeCode(this.bindings.restart);
  }

  consumeJoin(): boolean {
    this.pollGamepads();
    return this.consumeCode('Enter') || this.gamepadPressed.has('1:9');
  }

  capture(action: Action): Promise<string> {
    return new Promise((resolve) => {
      this.captureResolver = (code) => {
        this.bindings[action] = code;
        resolve(code);
      };
    });
  }

  bindTouchControls(container: HTMLElement): void {
    container.querySelectorAll<HTMLElement>('[data-touch]').forEach((button) => {
      const action = button.dataset.touch;
      if (!action) return;
      const press = (event: PointerEvent): void => {
        event.preventDefault();
        button.setPointerCapture?.(event.pointerId);
        if (!this.touchHeld.has(action)) this.touchPressed.add(action);
        this.touchHeld.add(action);
        this.directionPriority.set(`touch:${action}`, ++this.inputSequence);
      };
      const release = (event: PointerEvent): void => {
        event.preventDefault();
        this.touchHeld.delete(action);
        if (!this.touchPressed.has(action)) this.directionPriority.delete(`touch:${action}`);
      };
      button.addEventListener('pointerdown', press);
      button.addEventListener('pointerup', release);
      button.addEventListener('pointercancel', release);
      button.addEventListener('pointerleave', release);
    });
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (this.captureResolver) {
      event.preventDefault();
      const resolver = this.captureResolver;
      this.captureResolver = null;
      resolver(event.code);
      return;
    }
    if (!this.held.has(event.code)) {
      this.pressed.add(event.code);
      this.directionPriority.set(`key:${event.code}`, ++this.inputSequence);
    }
    this.held.add(event.code);
    if (event.code.startsWith('Arrow') || event.code === 'Space') event.preventDefault();
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.held.delete(event.code);
    if (!this.pressed.has(event.code)) this.directionPriority.delete(`key:${event.code}`);
  };

  private keyboardFrame(player: 1 | 2, allowArrowFallback: boolean): InputFrame {
    if (player === 2) {
      const heldDirection = this.directionFromCodes(
        Object.entries(P2_KEYS).filter(([, action]) => ['up', 'down', 'left', 'right'].includes(action)).map(([code, action]) => [code, action as Direction]),
        1,
      );
      return {
        direction: this.directionRepeat[2].next(heldDirection),
        placeWall: this.isPressed('Numpad0') || this.gamepadPressed.has('1:0'),
        shatter: this.isPressed('Numpad1') || this.gamepadPressed.has('1:1'),
        special: this.isPressed('Numpad2') || this.gamepadPressed.has('1:2'),
      };
    }

    const candidates: [string, Direction][] = [
      [this.bindings.up, 'up'],
      [this.bindings.down, 'down'],
      [this.bindings.left, 'left'],
      [this.bindings.right, 'right'],
    ];
    if (allowArrowFallback) candidates.push(['ArrowUp', 'up'], ['ArrowDown', 'down'], ['ArrowLeft', 'left'], ['ArrowRight', 'right']);
    const heldDirection = this.directionFromCodes(candidates, 0) ?? this.touchDirection();
    return {
      direction: this.directionRepeat[1].next(heldDirection),
      placeWall: this.isPressed(this.bindings.wall) || this.touchPressed.has('wall') || this.gamepadPressed.has('0:0'),
      shatter: this.isPressed(this.bindings.shatter) || this.touchPressed.has('shatter') || this.gamepadPressed.has('0:1'),
      special: this.isPressed(this.bindings.special) || this.touchPressed.has('special') || this.gamepadPressed.has('0:2'),
    };
  }

  private directionFromCodes(candidates: readonly [string, Direction][], gamepadIndex: number): Direction | null {
    const directions: readonly Direction[] = ['up', 'down', 'left', 'right'];
    return pickLatestDirection([
      ...candidates.map(([code, direction]) => ({
        id: `key:${code}`,
        direction,
        active: this.held.has(code) || this.pressed.has(code),
        priority: this.directionPriority.get(`key:${code}`) ?? 0,
      })),
      ...directions.map((direction) => ({
        id: `pad:${gamepadIndex}:${direction}`,
        direction,
        active: this.gamepadHeld.has(`${gamepadIndex}:${direction}`),
        priority: this.directionPriority.get(`pad:${gamepadIndex}:${direction}`) ?? 0,
      })),
    ]);
  }

  private touchDirection(): Direction | null {
    const directions: readonly Direction[] = ['up', 'down', 'left', 'right'];
    return pickLatestDirection(directions.map((direction) => ({
      id: `touch:${direction}`,
      direction,
      active: this.touchHeld.has(direction) || this.touchPressed.has(direction),
      priority: this.directionPriority.get(`touch:${direction}`) ?? 0,
    })));
  }

  private isPressed(code: string): boolean {
    return this.pressed.has(code);
  }

  private consumeCode(code: string): boolean {
    const pressed = this.pressed.has(code);
    this.pressed.delete(code);
    return pressed;
  }

  private consumeGamepad(code: string): boolean {
    const pressed = this.gamepadPressed.has(code);
    this.gamepadPressed.delete(code);
    return pressed;
  }

  private pollGamepads(): void {
    const pads = navigator.getGamepads?.() ?? [];
    for (let index = 0; index < Math.min(2, pads.length); index += 1) {
      const pad = pads[index];
      if (!pad) continue;
      const active = new Set<string>();
      const x = pad.axes[0] ?? 0;
      const y = pad.axes[1] ?? 0;
      if (x < -0.4) active.add(`${index}:left`);
      if (x > 0.4) active.add(`${index}:right`);
      if (y < -0.4) active.add(`${index}:up`);
      if (y > 0.4) active.add(`${index}:down`);
      if (pad.buttons[12]?.pressed) active.add(`${index}:up`);
      if (pad.buttons[13]?.pressed) active.add(`${index}:down`);
      if (pad.buttons[14]?.pressed) active.add(`${index}:left`);
      if (pad.buttons[15]?.pressed) active.add(`${index}:right`);
      for (const button of [0, 1, 2, 9]) if (pad.buttons[button]?.pressed) active.add(`${index}:${button}`);
      for (const code of active) if (!this.gamepadHeld.has(code)) {
        this.gamepadPressed.add(code);
        this.directionPriority.set(`pad:${code}`, ++this.inputSequence);
      }
      for (const code of [...this.gamepadHeld]) if (code.startsWith(`${index}:`) && !active.has(code)) {
        this.gamepadHeld.delete(code);
        this.directionPriority.delete(`pad:${code}`);
      }
      for (const code of active) this.gamepadHeld.add(code);
    }
  }
}
